/**
 * Non-React access to the current permission set.
 *
 * `checkAccess.ts` is called from 50+ modules as plain functions, including
 * from redux slices, so it cannot use hooks. Reading the store directly would
 * create a cycle (checkAccess -> store -> sideMenuSlice -> checkAccess), so
 * the set is mirrored here instead. This module imports nothing.
 *
 * Like the localStorage reads it replaces, a change here does not by itself
 * re-render anything — components that need reactivity use usePermissions().
 */

/** One screen this user can open, as served by /roles/me. */
export interface ReachableSurface {
  id: string;
  label: string;
  /** Relative to the client segment. */
  path: string;
  section: string;
  canEdit: boolean;
}

let permissions = new Set<string>();
let surfaces: ReachableSurface[] = [];
let everySurfacePath: string[] = [];
let role: string | null = null;
let custom = false;
let resolved = false;

export const setPermissionRegistry = (
  next: string[],
  nextRole: string | null,
  isCustomRole = false,
  nextSurfaces: ReachableSurface[] = [],
  nextAllPaths: string[] = []
): void => {
  permissions = new Set(next);
  // Copied: callers may hand us a state-managed array.
  surfaces = nextSurfaces.map((s) => ({ ...s }));
  everySurfacePath = [...nextAllPaths];
  role = nextRole;
  custom = isCustomRole;
  resolved = true;
};

/**
 * True when the signed-in user holds a custom role.
 *
 * Mirrors the backend rule: custom roles are governed purely by permissions,
 * built-in roles keep their original behaviour.
 */
export const isOnCustomRole = (): boolean => custom;

/** Screens this user can open. Empty until /roles/me returns. */
export const getReachableSurfaces = (): ReachableSurface[] => surfaces;

/**
 * Where to send this user when they have no destination of their own — on
 * login, and instead of bouncing them to a page they cannot open.
 */
export const getLandingPath = (): string | null => surfaces[0]?.path ?? null;

/** Strip the encrypted client id a URL carries, leaving the screen's own path. */
const withoutClient = (pathname: string): string => {
  const parts = pathname.split('?')[0].split('/').filter(Boolean);
  if (!parts.length) return '/';
  // The client id is the only segment that is not part of any screen path.
  const candidate = '/' + parts.join('/');
  if (everySurfacePath.includes(candidate)) return candidate;
  const dropped = '/' + parts.slice(1).join('/');
  return everySurfacePath.includes(dropped) ? dropped : candidate;
};

/**
 * Whether this user may open the screen at `pathname`.
 *
 * Returns null when the path is not a screen — a sub-page, or anything the
 * catalogue does not describe — so the caller falls back to its permission
 * check rather than refusing something it cannot judge.
 *
 * This exists because permissions cannot answer the question. Locations needs
 * branches:read and regions:read, and Overview's dependency closure grants
 * both, so a role given only Overview passes any key-based test for Locations.
 * What it does not have is Locations among the screens it was granted.
 */
export const mayOpenSurface = (pathname: string): boolean | null => {
  if (!resolved || !custom || !everySurfacePath.length) return null;
  const path = withoutClient(pathname);
  if (!everySurfacePath.includes(path)) return null;
  return surfaces.some((s) => s.path.split('?')[0] === path);
};

export const clearPermissionRegistry = (): void => {
  everySurfacePath = [];
  permissions = new Set();
  surfaces = [];
  role = null;
  custom = false;
  resolved = false;
};

/** True only when the permission is actually held. Fails closed. */
export const hasPermission = (key: string): boolean => permissions.has(key);

export const hasAnyPermission = (...keys: string[]): boolean =>
  keys.some((k) => permissions.has(k));

/**
 * Server-provided legacy role string. Used only by the shim helpers whose
 * meaning is role identity rather than capability — see checkAccess.ts.
 */
export const getResolvedRole = (): string | null => role;

/** False until /roles/me has returned at least once. */
export const isPermissionRegistryReady = (): boolean => resolved;
